import { useEffect } from "react";

const DEFAULT_TITLE = "HL Software — Desenvolvimento de SaaS e Automação";

function upsertMeta(selector, attr, value) {
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        const [attrName] = attr.split("=");
        const attrValue = attr.split("=")[1]?.replace(/"/g, "");
        el.setAttribute(attrName, attrValue || "");
        document.head.appendChild(el);
    }
    el.setAttribute("content", value);
}

function upsertLink(rel, href) {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function upsertJsonLd(data) {
    let el = document.querySelector('script[type="application/ld+json"][data-seo]');
    if (!el) {
        el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-seo", "true");
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(
        Array.isArray(data) ? { "@context": "https://schema.org", "@graph": data } : data
    );
}

function removeJsonLd() {
    const el = document.querySelector('script[type="application/ld+json"][data-seo]');
    if (el) el.remove();
}

/**
 * Props:
 *  title        – <title> e og:title / twitter:title
 *  description  – meta description e OG/Twitter
 *  canonical    – link canonical e og:url
 *  ogImage      – og:image e twitter:image (URL absoluta, 1200×630 recomendado)
 *  ogType       – og:type ("website" padrão; "article" para posts de blog)
 *  robots       – conteúdo do meta robots (ex.: "noindex,follow")
 *  jsonLd       – objeto ou array de objetos Schema.org (sem @context no array)
 *  articleMeta  – { publishedTime, modifiedTime, author, section } para og:article:*
 */
export default function SEOHead({
    title,
    description,
    canonical,
    ogImage,
    ogType = "website",
    robots,
    jsonLd,
    articleMeta,
}) {
    useEffect(() => {
        document.title = title || DEFAULT_TITLE;

        if (description) {
            upsertMeta('meta[name="description"]', 'name="description"', description);
            upsertMeta('meta[property="og:description"]', 'property="og:description"', description);
            upsertMeta('meta[name="twitter:description"]', 'name="twitter:description"', description);
        }

        if (title) {
            upsertMeta('meta[property="og:title"]', 'property="og:title"', title);
            upsertMeta('meta[name="twitter:title"]', 'name="twitter:title"', title);
        }

        upsertMeta('meta[property="og:type"]', 'property="og:type"', ogType);

        if (canonical) {
            upsertLink("canonical", canonical);
            upsertMeta('meta[property="og:url"]', 'property="og:url"', canonical);
        }

        if (ogImage) {
            upsertMeta('meta[property="og:image"]', 'property="og:image"', ogImage);
            upsertMeta('meta[name="twitter:image"]', 'name="twitter:image"', ogImage);
            upsertMeta('meta[name="twitter:card"]', 'name="twitter:card"', "summary_large_image");
        }

        if (robots) {
            upsertMeta('meta[name="robots"]', 'name="robots"', robots);
        }

        if (articleMeta) {
            if (articleMeta.publishedTime)
                upsertMeta('meta[property="article:published_time"]', 'property="article:published_time"', articleMeta.publishedTime);
            if (articleMeta.modifiedTime)
                upsertMeta('meta[property="article:modified_time"]', 'property="article:modified_time"', articleMeta.modifiedTime);
            if (articleMeta.author)
                upsertMeta('meta[property="article:author"]', 'property="article:author"', articleMeta.author);
            if (articleMeta.section)
                upsertMeta('meta[property="article:section"]', 'property="article:section"', articleMeta.section);
        }

        if (jsonLd) {
            upsertJsonLd(jsonLd);
        }

        return () => {
            document.title = DEFAULT_TITLE;
            upsertMeta('meta[property="og:type"]', 'property="og:type"', "website");
            if (robots) {
                upsertMeta('meta[name="robots"]', 'name="robots"', "index,follow,max-image-preview:large");
            }
            if (jsonLd) {
                removeJsonLd();
            }
        };
    }, [title, description, canonical, ogImage, ogType, robots, jsonLd, articleMeta]);

    return null;
}
